// useNavCounts — the outstanding-work tallies behind the sidebar badges
// (SEP-210, widened to Ops and Compliance by SEP-208).
//
// The sidebar is always mounted, so it must not pull the overdrawn-cash,
// corporate-action and onboarding list payloads into the shell to count them.
// It asks the server for three numbers. This is the same reason
// `use-review-queue-counts.ts` exists beside the full review-queue model
// (PRD #156) — the page hooks stay the source of truth for the pages.
//
// The counts fall back to no counts while loading and on error, so a slow or
// failing endpoint hides the badges rather than blocking navigation. A count
// of a feature that the Feature Manifest turns off has no key (ADR-0027).

import type { NavCountsResponse, NavSeenKey } from '@septima/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export const navKeys = {
    counts: () => ['nav-counts'] as const,
};

const NO_COUNTS: NavCountsResponse = {};

/**
 * Poll the sidebar badge counts. Call it from the Desk, Ops and Comply shells
 * only: the endpoint is persona-gated to those three (plus im-assistant), and
 * the View shell has no badged row. The two Desk counts read zero for an Ops or
 * a Comply caller. A minute is the right cadence — the numbers move when
 * somebody resolves a row or advances a case, not second by second.
 */
export function useNavCounts(enabled: boolean): NavCountsResponse {
    const { data } = useQuery({
        queryKey: navKeys.counts(),
        queryFn: async () => {
            const res = await api.nav.counts();
            if (res.status !== 200) {
                throw new Error(`nav counts fetch failed: ${res.status}`);
            }
            return res.body;
        },
        refetchInterval: 60_000,
        enabled,
    });
    return data ?? NO_COUNTS;
}

/**
 * Stamp that the user has looked at a badged row, then refresh the counts so
 * the badge clears without a reload. Best-effort: a failed stamp leaves the
 * badge up, which is the safe way round.
 */
export function useMarkNavSeen() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (navKey: NavSeenKey) => {
            const res = await api.nav.markSeen({ body: { navKey } });
            if (res.status !== 200) {
                throw new Error(`nav seen stamp failed: ${res.status}`);
            }
            return res.body;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: navKeys.counts() });
        },
    });
}
